/**
 * Editor для Акту виробничих послуг (Порція Виробництво.4b).
 *
 * Найпростіший виробничий editor — без матеріальної частини, лише header
 * + одна табличка послуг. Patterns точно як у GoodsSaleEditor:
 * - id='new' для CREATE, UUID для EDIT
 * - useNumberPreview placeholder для нового документа
 * - usePermissionsByOrg('production.service_act.manage')
 * - disabled={isPosted} для всіх полів і таблиці
 * - RequirePermission навколо кнопок Save/Post/Unpost
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { accounts as accountsApi } from '@/api/documents';
import {
  productionServiceActs,
  type ProductionServiceActItemIn,
} from '@/api/productionServiceActs';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const PERMISSION = 'production.service_act.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  contract_id: string;
  service_amount: string;
  vat_amount: string;
  revenue_account_code: string | null;
  receivable_account_code: string | null;
  cost_account_code: string | null;
  comment: string;
  items: ProductionServiceActItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customer_contractor_id: '',
  contract_id: '',
  service_amount: '0',
  vat_amount: '0',
  revenue_account_code: null,
  receivable_account_code: null,
  cost_account_code: null,
  comment: '',
  items: [],
};

const newRow = (n: number): ProductionServiceActItemIn => ({
  line_number: n,
  nomenclature_id: '',
  quantity: '1',
  price: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function ProductionServiceActEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const accountsQ = useQuery({
    queryKey: ['accounts-postable'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 1000 }),
  });
  const acctList = accountsQ.data ?? [];

  const docQuery = useQuery({
    queryKey: ['production-service-act', id],
    queryFn: () => productionServiceActs.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const numberPreview = useNumberPreview(
    'doc.production_service_acts',
    form.organization_id,
    isNew,
  );

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customer_contractor_id: d.customer_contractor_id,
        contract_id: d.contract_id ?? '',
        service_amount: d.service_amount,
        vat_amount: d.vat_amount,
        revenue_account_code: d.revenue_account_code ?? null,
        receivable_account_code: d.receivable_account_code ?? null,
        cost_account_code: d.cost_account_code ?? null,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity ?? '1',
          price: it.price,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const isPosted = docQuery.data?.posted ?? false;

  const acctSelect = (
    value: string | null | undefined,
    onChange: (v: string | null) => void,
    def: string,
  ) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} disabled={isPosted}>
      <option value="">— (деф. {def})</option>
      {acctList.map((a) => (<option key={a.id} value={a.code}>{a.code} — {a.name}</option>))}
    </select>
  );

  const totals = useMemo(() => {
    const svc = Number(form.service_amount) || 0;
    const vat = Number(form.vat_amount) || 0;
    return { svc, vat, total: svc + vat };
  }, [form.service_amount, form.vat_amount]);

  const create = useMutation({
    mutationFn: () =>
      productionServiceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
      navigate(`/production-service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      productionServiceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        items: form.items,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  // Новий: створити і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await productionServiceActs.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      await productionServiceActs.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
      navigate(`/production-service-acts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });

  // Збережений непроведений: зберегти правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await productionServiceActs.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customer_contractor_id: form.customer_contractor_id,
        contract_id: form.contract_id || null,
        service_amount: form.service_amount,
        vat_amount: form.vat_amount,
        revenue_account_code: form.revenue_account_code,
        receivable_account_code: form.receivable_account_code,
        cost_account_code: form.cost_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      await productionServiceActs.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => productionServiceActs.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-service-act', id] });
      qc.invalidateQueries({ queryKey: ['production-service-acts'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  function addItem() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items
        .filter((_, idx) => idx !== i)
        .map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<ProductionServiceActItemIn>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий акт виробничих послуг' : `Акт: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/production-service-acts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => (isNew ? createAndPost.mutate() : updateAndPost.mutate())}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          deletable={false}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code} — {o.name}
              </option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>

        <div className="field">
          <label>Номер</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>

        <div className="field">
          <label>Дата</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>

        <div className="field">
          <label>Замовник</label>
          <select
            value={form.customer_contractor_id}
            onChange={(e) => setForm({ ...form, customer_contractor_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {cQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Сума послуги</label>
          <input
            type="number"
            step="0.01"
            value={form.service_amount}
            onChange={(e) => setForm({ ...form, service_amount: e.target.value })}
            disabled={isPosted}
          />
        </div>

        <div className="field">
          <label>ПДВ</label>
          <input
            type="number"
            step="0.01"
            value={form.vat_amount}
            onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
            disabled={isPosted}
          />
        </div>

        <div className="field">
          <label>Рахунок доходів</label>
          {acctSelect(form.revenue_account_code, (v) => setForm({ ...form, revenue_account_code: v }), '703')}
        </div>
        <div className="field">
          <label>Рахунок розрахунків</label>
          {acctSelect(form.receivable_account_code, (v) => setForm({ ...form, receivable_account_code: v }), '361')}
        </div>
        <div className="field">
          <label>Рахунок затрат</label>
          {acctSelect(form.cost_account_code, (v) => setForm({ ...form, cost_account_code: v }), '231')}
        </div>

        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>
        Послуги (рядки) — разом з ПДВ: {totals.total.toFixed(2)}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Послуга</th>
            <th style={{ padding: '0.5rem', width: 100 }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select
                  value={it.nomenclature_id}
                  onChange={(e) => setItemField(i, { nomenclature_id: e.target.value })}
                  disabled={isPosted}
                >
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.code} — {n.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number"
                  step="0.001"
                  value={it.quantity}
                  onChange={(e) => setItemField(i, { quantity: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input
                  type="number"
                  step="0.01"
                  value={it.price}
                  onChange={(e) => setItemField(i, { price: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeItem(i)} title="Видалити">
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!isPosted && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
          + Додати рядок
        </button>
      )}

      {error && (
        <div className="error" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
